import { connectedDecisionCitationId, connectedFactCitationId, } from "./subject-connected-knowledge.js";
import { isOpenQuestionResolved } from "./subject-intelligence.js";
export const SUBJECT_ASK_PROMPT_CHAR_BUDGET = 24_000;
/** Floor remaining for body + evidence + related + ops after core + connected. */
export const SUBJECT_ASK_CANONICAL_RESERVE = 10_000;
export const SUBJECT_ASK_CONNECTED_CHAR_BUDGET = 8_000;
export const SUBJECT_ASK_DISCIPLINE = [
    "SUBJECT ASK DISCIPLINE:",
    "Answer only from the supplied Subject context unless the user also pinned additional sources.",
    "CANONICAL KNOWLEDGE is structured truth recorded on the pinned Subject (identity, facts, decisions, graph questions).",
    "CONNECTED KNOWLEDGE is structured truth recorded on named origin Subjects that are explicitly included for this pin. It may inform answers about the pinned Subject. Attribute connected claims to the named origin. Never describe a connected fact as recorded on the pinned Subject unless the same fact also appears under CANONICAL KNOWLEDGE.",
    "EVIDENCE is sourceRefs / retrieved excerpts for the pinned Subject. Origin evidence belongs to its origin. RELATED OPERATIONAL MATERIAL is associations — an association is not proof.",
    "A related Subject summary is not evidence and does not confer knowledge scope. Do not infer additional programme membership or knowledge-scope includes.",
    "Deterministic Attention and What Changed are derived views on the pinned Subject, not extra canonical facts.",
    "Do not treat an open question as resolved unless the context shows an addresses edge.",
    "Do not infer CRM identity from matching names. Do not manufacture missing facts.",
    "Do not silently mutate canonical knowledge. If the Subject material does not answer the question, say so.",
    "To record a Subject FACT, confirm propose_subject_fact with an explicit subjectId. That creates pending knowledge for staff review — it is not canonical until Accept. Do not say the fact is saved as current knowledge. Do not use capture_to_ideation for Subject facts.",
    "Distinguish your synthesis from canonical statements.",
    "Cite bundle citation IDs (fact:…, decision:…, question:…, evidence:…, assoc:…, connected-fact:…, connected-decision:…, connected-evidence:…) for substantive claims.",
    "Material labelled OVERFLOW is additional retrieved organisational material, not canonical Subject knowledge.",
    "If CONNECTED KNOWLEDGE is marked truncated/bounded, do not infer absence from omitted origin material and do not search elsewhere.",
].join(" ");
function clip(text, max) {
    if (text.length <= max)
        return text;
    return `${text.slice(0, Math.max(0, max - 1))}…`;
}
const CONNECTED_BOUNDED = "Connected knowledge is bounded/truncated in this context. Do not infer absence from origin material that is not present here. Do not search elsewhere.";
function originEvidenceRefs(origin) {
    const seen = new Set();
    const refs = [];
    for (const fact of origin.facts) {
        for (const row of fact.evidence) {
            if (seen.has(row.citationId))
                continue;
            seen.add(row.citationId);
            refs.push(row);
        }
    }
    return refs;
}
function renderOriginBlock(origin) {
    const citations = [];
    const lines = [
        `### Origin: ${origin.label} (kind=${origin.kind})`,
        "Scope: explicitly included knowledge",
        `Recorded on: ${origin.label}`,
    ];
    if (origin.superseded) {
        lines.push("This origin Subject is superseded; no current connected facts/decisions.");
    }
    if (origin.facts.length > 0) {
        lines.push("Current facts:");
        for (const fact of origin.facts) {
            const citationId = connectedFactCitationId(origin.originSubjectId, fact.citationId);
            const evidenceIds = fact.evidence.map((row) => `[${row.citationId}]`).join(" ");
            const line = `- [${citationId}] ${fact.key} = ${fact.value}${fact.unit ? ` ${fact.unit}` : ""}`;
            const meta = `  asOf=${fact.asOf} source=${fact.source}${evidenceIds ? `\n  origin evidence: ${evidenceIds}` : ""}`;
            lines.push(line, meta);
            citations.push({
                citationId,
                title: `${origin.label} · ${fact.key}`,
                excerpt: clip(`${fact.value}${fact.unit ? ` ${fact.unit}` : ""} asOf=${fact.asOf}`, 400),
            });
        }
    }
    if (origin.decisions.length > 0) {
        lines.push("Decisions:");
        for (const decision of origin.decisions) {
            const citationId = connectedDecisionCitationId(origin.originSubjectId, decision.citationId);
            lines.push(`- [${citationId}] ${decision.at}: ${decision.text}`);
            citations.push({
                citationId,
                title: `${origin.label} · decision`,
                excerpt: clip(decision.text, 400),
            });
        }
    }
    const evidence = originEvidenceRefs(origin);
    if (evidence.length > 0) {
        lines.push("Origin evidence:");
        for (const row of evidence) {
            lines.push(`- [${row.citationId}] ${row.kind} ref=${row.ref}${row.span ? ` span=${row.span}` : ""}`);
            citations.push({
                citationId: row.citationId,
                title: `${origin.label} · origin evidence`,
                excerpt: clip(`${row.kind} ${row.ref}${row.span ? ` ${row.span}` : ""}`, 400),
            });
        }
    }
    return { block: lines.join("\n"), citations };
}
export function projectSubjectContextForAsk(bundle, budget = SUBJECT_ASK_PROMPT_CHAR_BUDGET, options = { includeConnected: true }) {
    const citations = [];
    const seenCite = new Set();
    const parts = [];
    let used = 0;
    let truncated = false;
    const push = (block) => {
        if (!block)
            return true;
        const remaining = budget - used;
        if (remaining <= 0) {
            truncated = true;
            return false;
        }
        const next = block.length <= remaining ? block : clip(block, remaining);
        if (next.length < block.length)
            truncated = true;
        parts.push(next);
        used += next.length;
        return next.length === block.length;
    };
    const cite = (citationId, title, excerpt) => {
        if (seenCite.has(citationId))
            return;
        seenCite.add(citationId);
        citations.push({ citationId, title, excerpt: clip(excerpt, 400) });
    };
    const ident = bundle.canonical.identity;
    const identityBlock = [
        "## CANONICAL KNOWLEDGE",
        `[${ident.citationId}] Subject ${ident.label} (kind=${ident.kind}, id=${ident.id})`,
        bundle.canonical.summary.text
            ? `[${bundle.canonical.summary.citationId}] Summary: ${bundle.canonical.summary.text}`
            : "No summary is recorded.",
    ].join("\n");
    cite(ident.citationId, ident.label, bundle.canonical.summary.text || ident.label);
    if (bundle.canonical.summary.text) {
        cite(bundle.canonical.summary.citationId, `${ident.label} summary`, bundle.canonical.summary.text);
    }
    push(identityBlock);
    if (bundle.canonical.facts.length > 0) {
        const lines = ["Canonical facts:"];
        for (const fact of bundle.canonical.facts) {
            const line = `[${fact.citationId}] ${fact.key}=${fact.value}${fact.unit ? ` ${fact.unit}` : ""} source=${fact.source} asOf=${fact.asOf}`;
            lines.push(line);
            cite(fact.citationId, `Fact ${fact.key}`, line);
        }
        push(lines.join("\n"));
    }
    if (bundle.canonical.decisions.length > 0) {
        const lines = ["Decisions:"];
        for (const decision of bundle.canonical.decisions) {
            const line = `[${decision.citationId}] ${decision.at}: ${decision.text}`;
            lines.push(line);
            cite(decision.citationId, "Decision", decision.text);
        }
        push(lines.join("\n"));
    }
    const open = bundle.canonical.openQuestions.filter((row) => !isOpenQuestionResolved(row.nodeId, bundle.canonical.related));
    if (open.length > 0) {
        const lines = ["Unresolved open questions (not resolved by prose/facts/decisions):"];
        for (const question of open) {
            const line = `[${question.citationId}] ${question.label}`;
            lines.push(line);
            cite(question.citationId, "Open question", question.label);
        }
        push(lines.join("\n"));
    }
    const intel = bundle.intelligence;
    const derived = [
        "## DETERMINISTIC DERIVED CONTEXT (not canonical facts)",
        intel.attention.length === 0
            ? "Attention: none."
            : `Attention:\n${intel.attention.map((item) => `- [${item.category}] ${item.summary}`).join("\n")}`,
        intel.changes.length === 0
            ? `What changed (${intel.window.label}): none.`
            : `What changed (${intel.window.label}):\n${intel.changes
                .slice(0, intel.previewLimit)
                .map((item) => `- ${item.at.slice(0, 10)} ${item.summary}`)
                .join("\n")}`,
    ].join("\n");
    push(derived);
    const sparse = !bundle.canonical.summary.text &&
        !bundle.canonical.body.text &&
        bundle.canonical.facts.length === 0 &&
        bundle.canonical.decisions.length === 0 &&
        open.length === 0;
    if (sparse) {
        push("This Subject is sparse. If the question is not answered by the material above, say the information is unavailable. Do not invent facts.");
    }
    if (options.includeConnected && bundle.connected.origins.length > 0) {
        const connectedCap = Math.min(SUBJECT_ASK_CONNECTED_CHAR_BUDGET, Math.max(0, budget - used - SUBJECT_ASK_CANONICAL_RESERVE));
        let connectedUsed = 0;
        let connectedTruncated = Boolean(bundle.connected.truncated || bundle.connected.origins.some((row) => row.truncatedFacts));
        const connectedParts = [];
        const header = [
            "## CONNECTED KNOWLEDGE",
            "Knowledge below is recorded on explicitly included origin Subjects.",
            "It is not canonical knowledge of the pinned Subject.",
            "Do not say the pinned Subject records a connected fact unless that fact also appears under CANONICAL KNOWLEDGE.",
        ].join("\n");
        const takeConnected = (block) => {
            const extra = connectedParts.length > 0 ? 2 : 0;
            if (connectedUsed + extra + block.length > connectedCap) {
                connectedTruncated = true;
                truncated = true;
                return false;
            }
            if (used + extra + block.length > budget) {
                connectedTruncated = true;
                truncated = true;
                return false;
            }
            connectedParts.push(block);
            connectedUsed += extra + block.length;
            used += extra + block.length;
            return true;
        };
        if (takeConnected(header)) {
            for (const origin of bundle.connected.origins) {
                const rendered = renderOriginBlock(origin);
                if (!takeConnected(rendered.block))
                    break;
                for (const row of rendered.citations)
                    cite(row.citationId, row.title, row.excerpt);
            }
        }
        if (connectedTruncated)
            takeConnected(CONNECTED_BOUNDED);
        parts.push(...connectedParts);
    }
    if (bundle.canonical.body.text) {
        push(`[${bundle.canonical.body.citationId}] Body:\n${bundle.canonical.body.text}`);
        cite(bundle.canonical.body.citationId, `${ident.label} body`, bundle.canonical.body.text);
    }
    const evidence = bundle.canonical.evidence.filter((row) => row.unavailableReason !== "restricted");
    const evidenceLines = [
        "## EVIDENCE",
        "Evidence is sourceRefs. Items the caller cannot access are omitted. An association is not evidence.",
    ];
    for (const row of evidence) {
        const line = `[${row.citationId}] ${row.kind} status=${row.status}${row.title ? ` title=${row.title}` : ""}${row.unavailableReason ? ` (${row.unavailableReason})` : ""}`;
        evidenceLines.push(line);
        cite(row.citationId, row.title ?? `${row.kind} evidence`, line);
    }
    push(evidenceLines.join("\n"));
    const related = bundle.canonical.related.filter((row) => row.relation !== "questions");
    if (related.length > 0) {
        const lines = [
            "## RELATED SUBJECTS (not evidence for the active Subject)",
            ...related.map((row) => `[${row.citationId}] ${row.label} (${row.kind}, ${row.relation}) ${row.excerpt}`),
        ];
        for (const row of related) {
            cite(row.citationId, row.label, row.excerpt || row.label);
        }
        push(lines.join("\n"));
    }
    const assocLines = [
        "## RELATED OPERATIONAL MATERIAL (associations are not evidence)",
        bundle.canonical.associations.length === 0
            ? "No operational associations."
            : bundle.canonical.associations
                .map((row) => `[${row.citationId}] ${row.objectType} ${row.title}`)
                .join("\n"),
    ];
    for (const row of bundle.canonical.associations) {
        cite(row.citationId, row.title, `${row.objectType} ${row.title}`);
    }
    push(assocLines.join("\n"));
    const excerpts = bundle.retrieval.excerpts.filter((row) => row.unavailableReason !== "restricted");
    if (excerpts.length > 0) {
        const lines = ["## RETRIEVED EXCERPTS"];
        for (const row of excerpts) {
            const layer = row.layer === "association" ? "operational" : "evidence";
            lines.push(`[${row.citationId}] (${layer}) ${row.title ?? row.kind} status=${row.status}`);
            if (row.text)
                lines.push(row.text);
            cite(row.citationId, row.title ?? `${row.kind} excerpt`, row.text ?? row.kind);
        }
        push(lines.join("\n"));
    }
    if (truncated) {
        parts.push("\n[Subject prompt truncated to budget; canonical facts were packed before operational prose.]");
    }
    return {
        title: ident.label,
        prompt: parts.join("\n\n"),
        truncated,
        citations,
    };
}
//# sourceMappingURL=subject-ask-projection.js.map